import { APP_DOMAIN, APP_URL } from './env';

// Which project this browser tab is looking at, according to its
// hostname.
//
// A project's slug is a DNS label — models/project.js has said so since
// step 2, and the settings screen has printed `<slug>.<domain>` as the
// project's ADDRESS for just as long. This is the function that finally
// makes that address mean something.
//
// The console keeps its own host. Everything else under the app domain
// that is not a reserved name is a project.

const RESERVED = new Set([
  'console',
  'api',
  'www',
  'developer',
  'docs',
  'usercontent',
  'preview',
  'status',
  'share',
  'staging',
  'assets',
  'cdn',
  'app',
  'auth',
  'login',
  'signup',
  'billing',
  'settings',
  'admin',
  'hauser',
]);

const SLUG_REG = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

// Exported for tests: the host resolution, with no window involved.
export function slugFromHost(host, domain = APP_DOMAIN) {
  const name = String(host || '')
    .split(':')[0]
    .toLowerCase();
  const base = String(domain || '')
    .split(':')[0]
    .toLowerCase();
  if (!name || !base || !name.endsWith(`.${base}`)) {
    return null;
  }
  const label = name.slice(0, -(base.length + 1));
  if (!label || label.includes('.') || RESERVED.has(label) || !SLUG_REG.test(label)) {
    return null;
  }
  return label;
}

export function currentProjectSlug(location) {
  const target =
    location || (typeof window === 'undefined' ? null : window.location);
  return target ? slugFromHost(target.hostname) : null;
}

export function isProjectHost(location) {
  return currentProjectSlug(location) !== null;
}

// The address a project lives at. One builder, so nothing constructs a
// project URL by hand and drifts from what the API will hand a session
// to (lib/handoff.js derives the same origin server-side, and the two
// must agree or the redeem is refused).
export function projectOrigin(slug) {
  const { protocol, port } = new URL(APP_URL);
  const host = String(APP_DOMAIN).split(':')[0];
  const suffix = port ? `:${port}` : '';
  return `${protocol}//${slug}.${host}${suffix}`;
}

export function projectUrl(slug, path = '/') {
  return `${projectOrigin(slug)}${path.startsWith('/') ? path : `/${path}`}`;
}
